"use client";

import { Link, useLocation } from "react-router-dom";
import {
  Compass,
  Car,
  Map,
  Bell,
  Camera,
  LogOut,
  Sun,
  Moon,
  Monitor,
  Plus,
  Menu, } from '@/ui/icons'
import { Button } from "@/ui/components";
import { Logo } from "@/ui/components";
import { useAuth } from "@/modules/auth";
import { useTheme, type ThemePreference } from "@/contexts/ThemeContext";
import { useNotificationsPanel } from "@/contexts/NotificationsPanelContext";
import { useRangerPanel } from "@/contexts/RangerPanelContext";
import { useAppNav } from "@/contexts/AppNavContext";
import { useBackendNotificationsRealtime } from "@/shared/hooks/useNotificationsRealtime";
import { NotificationTrigger } from "@/ui/components/notifications/NotificationTrigger";
import { RangerMark } from "@/modules/ai/components/RangerMark";
import { cn } from "@/shared/utils";
import { getUserProfileUrl } from "@/shared/utils/userImages";
import { useCreateModal } from "@/contexts/CreateModalContext";
import { useSubscription } from "@/modules/subscriptions";

const primaryNavItems = [
  { title: "Trips", href: "/trips", icon: Compass, match: (path: string) => path === "/trips" || path.startsWith("/trips/") },
  { title: "Garage", icon: Car, comingSoon: true },
  { title: "Explore", icon: Map, comingSoon: true },
  { title: "Photos", href: "/photos", icon: Camera, match: (path: string) => path === "/photos" || path.startsWith("/photos/") },
];

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500";

/** Sits right after its target, which must carry the `peer` class. */
function RailTooltip({ label }: { label: string }) {
  return (
    <span className="absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-earth-900 px-2 py-1 text-xs font-medium text-background opacity-0 pointer-events-none transition-opacity duration-100 peer-hover:opacity-100 peer-focus-visible:opacity-100">
      {label}
    </span>
  );
}

interface RailItemProps {
  label: string;
  /** Spoken name and collapsed tooltip, when they should say more than the label. */
  description?: string;
  icon?: React.ElementType;
  /** Replaces the icon, for the logo mark, the avatar and the unread count. */
  leading?: React.ReactNode;
  /** Shown at the end of the row when the nav is expanded. */
  trailing?: React.ReactNode;
  to?: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  hasPopup?: boolean;
}

function RailItem({
  label,
  description,
  icon: Icon,
  leading,
  trailing,
  to,
  onClick,
  active = false,
  disabled = false,
  hasPopup = false,
}: RailItemProps) {
  const { expanded } = useAppNav();
  const className = cn(
    "peer flex h-10 w-full items-center gap-3 rounded-md px-2.5 text-left text-sm transition-colors",
    FOCUS_RING,
    active
      ? "bg-earth-100 font-medium text-earth-900"
      : disabled
        ? "cursor-not-allowed text-earth-500"
        : "text-earth-700 hover:bg-earth-50 hover:text-earth-900"
  );
  const content = (
    <>
      {leading ?? (Icon && <Icon className="h-5 w-5 shrink-0" aria-hidden />)}
      {expanded && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {expanded && trailing != null && (
        <span className="shrink-0 text-xs font-normal text-earth-600">{trailing}</span>
      )}
    </>
  );
  const name = description ?? label;

  return (
    <div className="relative">
      {to ? (
        <Link to={to} className={className} aria-current={active ? "page" : undefined} aria-label={name}>
          {content}
        </Link>
      ) : (
        <button
          type="button"
          className={className}
          onClick={onClick}
          disabled={disabled}
          aria-label={name}
          aria-haspopup={hasPopup ? "dialog" : undefined}
        >
          {content}
        </button>
      )}
      {!expanded && <RailTooltip label={name} />}
    </div>
  );
}

function UnreadCount({ count }: { count: number }) {
  return (
    <span className="block h-4 min-w-4 rounded-full bg-destructive px-1 text-center text-[10px] font-medium leading-4 text-destructive-foreground">
      {count > 99 ? "99+" : count}
    </span>
  );
}

function RailRanger() {
  const { isOpen, toggleRangerPanel } = useRangerPanel();

  return (
    <RailItem
      label="Ranger"
      description="Ranger AI Assistant"
      leading={<RangerMark className="h-5 w-5 shrink-0 text-adventure-600" />}
      onClick={toggleRangerPanel}
      active={isOpen}
    />
  );
}

function RailNotifications() {
  const { unreadCount } = useBackendNotificationsRealtime({ limit: 1 });
  const { openNotificationsPanel } = useNotificationsPanel();
  const { expanded } = useAppNav();
  const hasUnread = unreadCount > 0;

  return (
    <RailItem
      label="Notifications"
      description={hasUnread ? `Notifications, ${unreadCount} unread` : undefined}
      leading={
        <span className="relative shrink-0">
          <Bell className="h-5 w-5" aria-hidden />
          {hasUnread && !expanded && (
            <span className="absolute -right-2 -top-1.5">
              <UnreadCount count={unreadCount} />
            </span>
          )}
        </span>
      }
      trailing={hasUnread ? <UnreadCount count={unreadCount} /> : undefined}
      onClick={openNotificationsPanel}
      hasPopup
    />
  );
}

const THEME_CYCLE: ThemePreference[] = ["light", "dark", "system"];
const THEME_LABELS: Record<ThemePreference, string> = {
  light: "Light",
  dark: "Dark",
  system: "System",
};

export function AppSidebar() {
  const { user, logout } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const location = useLocation();
  const pathname = location.pathname;
  const { openCreateModal } = useCreateModal();
  const { isPro } = useSubscription();
  const { expanded, canExpand, toggle } = useAppNav();

  const handleSignOut = async () => {
    await logout();
  };

  const cycleTheme = () => {
    const idx = THEME_CYCLE.indexOf(theme);
    setTheme(THEME_CYCLE[(idx + 1) % THEME_CYCLE.length]);
  };

  const userId = user?.id ?? "";
  const ThemeIcon = theme === "system" ? Monitor : resolvedTheme === "dark" ? Moon : Sun;
  const themeTooltip = `Appearance: ${THEME_LABELS[theme]} (click to change)`;
  const fullName = user?.user_metadata?.full_name as string | undefined;
  const planLabel = isPro ? "Pro" : "Free";
  const initials =
    fullName?.slice(0, 2)?.toUpperCase() ?? user?.email?.slice(0, 2)?.toUpperCase() ?? "?";
  const toggleLabel = expanded ? "Collapse navigation" : "Expand navigation";

  return (
    <>
      {/* Desktop: flush left sidebar, an icon rail that expands to show labels */}
      <aside
        className={cn(
          "hidden md:flex flex-col fixed inset-y-0 left-0 z-40 border-r border-earth-200 bg-background px-2 py-2",
          expanded ? "w-60" : "w-14"
        )}
      >
        {canExpand && (
          <div className="relative mb-0.5 w-10">
            <button
              type="button"
              className={cn(
                "peer flex h-10 w-10 items-center justify-center rounded-md text-earth-700 transition-colors hover:bg-earth-50 hover:text-earth-900",
                FOCUS_RING
              )}
              onClick={toggle}
              aria-expanded={expanded}
              aria-label={toggleLabel}
            >
              <Menu className="h-5 w-5" aria-hidden />
            </button>
            {!expanded && <RailTooltip label={toggleLabel} />}
          </div>
        )}

        <nav className="flex flex-1 flex-col gap-0.5" aria-label="Main navigation">
          <RailItem
            label="Home"
            to="/dashboard"
            active={pathname === "/dashboard"}
            leading={
              <Logo to="" alt="" size="sm" className="-mx-0.5 h-6 w-6 shrink-0 dark:brightness-0 dark:invert" />
            }
          />
          {primaryNavItems.map((item) =>
            item.comingSoon ? (
              <RailItem
                key={item.title}
                label={item.title}
                description={`${item.title} · Coming soon`}
                icon={item.icon}
                trailing="Soon"
                disabled
              />
            ) : (
              <RailItem
                key={item.title}
                label={item.title}
                icon={item.icon}
                to={item.href}
                active={item.match?.(pathname)}
              />
            )
          )}
          <RailRanger />
          <RailNotifications />
        </nav>

        <div className="mx-0.5 my-2 border-t border-earth-200" />

        <div className="flex flex-col gap-0.5">
          <RailItem
            label="Appearance"
            description={themeTooltip}
            icon={ThemeIcon}
            trailing={THEME_LABELS[theme]}
            onClick={cycleTheme}
          />
          <RailItem label="Sign out" icon={LogOut} onClick={handleSignOut} />
          <RailItem
            label={fullName || user?.email || "Profile"}
            description={`Profile · ${planLabel}`}
            to={`/users/${userId}`}
            active={pathname === `/users/${userId}`}
            leading={
              <span className="-mx-0.5 flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-adventure-500/80 text-[10px] font-semibold text-primary-foreground">
                {user?.profile_image_path ? (
                  <img
                    src={getUserProfileUrl(user.profile_image_path)}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  initials
                )}
              </span>
            }
            trailing={planLabel}
          />
        </div>

        <div className="relative mt-2">
          <button
            type="button"
            className={cn(
              "peer flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-medium text-primary-foreground transition-[background-color,transform] duration-150 hover:bg-primary/90 active:scale-[0.97]",
              FOCUS_RING,
              "focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            )}
            onClick={openCreateModal}
            aria-label="Create"
          >
            <Plus className="h-5 w-5 shrink-0" aria-hidden />
            {expanded && "Create"}
          </button>
          {!expanded && <RailTooltip label="Create" />}
        </div>
      </aside>

      {/* Mobile: bottom nav bar */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 flex flex-row items-center justify-around py-2 px-2 bg-earth-900 border-t border-earth-700 z-40 safe-area-pb"
        role="navigation"
        aria-label="Main navigation"
      >
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-xl",
            pathname === "/trips" || pathname.startsWith("/trips/")
              ? "bg-adventure-600 text-primary-foreground"
              : "text-earth-200"
          )}
          asChild
          aria-current={pathname === "/trips" || pathname.startsWith("/trips/") ? "page" : undefined}
          aria-label="Trips"
        >
          <Link to="/trips">
            <Compass className="h-6 w-6" />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-300 opacity-80 cursor-not-allowed"
          disabled
          aria-label="Garage – Coming soon"
          title="Garage – Coming soon"
        >
          <Car className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-300 opacity-80 cursor-not-allowed"
          disabled
          aria-label="Explore – Coming soon"
          title="Explore – Coming soon"
        >
          <Map className="h-6 w-6" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "rounded-xl",
            pathname === "/photos" || pathname.startsWith("/photos/")
              ? "bg-adventure-600 text-primary-foreground"
              : "text-earth-200"
          )}
          asChild
          aria-current={pathname === "/photos" || pathname.startsWith("/photos/") ? "page" : undefined}
          aria-label="Photos"
        >
          <Link to="/photos">
            <Camera className="h-6 w-6" />
          </Link>
        </Button>
        <div className="relative flex items-center justify-center">
          <NotificationTrigger className="h-12 w-12 rounded-xl text-earth-200" />
        </div>
        <Button
          size="icon"
          className="h-11 w-11 rounded-full bg-adventure-600 text-primary-foreground hover:bg-adventure-700 shadow-md"
          onClick={openCreateModal}
          aria-label="Create"
        >
          <Plus className="h-5 w-5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-xl text-earth-200"
          onClick={cycleTheme}
          aria-label={themeTooltip}
          title={themeTooltip}
        >
          <ThemeIcon className="h-6 w-6" />
        </Button>
        <div className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl text-earth-200 overflow-hidden p-0"
            asChild
            aria-label="Profile"
          >
            <Link
              to={`/users/${userId}`}
              className="flex items-center justify-center h-10 w-10 rounded-xl overflow-hidden bg-adventure-500/80 text-primary-foreground font-semibold text-xs"
            >
              {user?.profile_image_path ? (
                <img
                  src={getUserProfileUrl(user.profile_image_path)}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                initials
              )}
            </Link>
          </Button>
          <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.5 text-[9px] font-bold rounded-full pointer-events-none leading-none ${isPro ? 'bg-adventure-600 text-white' : 'bg-earth-400 text-white'}`}>
            {isPro ? 'PRO' : 'FREE'}
          </span>
        </div>
      </nav>
    </>
  );
}
